<template>
  <ContentCard :loading="loadingUpdateProduct">
    <template #header>
      <ReviewsProductTabs />
    </template>

    <template #subheader>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsProductViewPricing }">
        {{ t('Reviews.product.edit.pricingTitle', { productName: product.title }) }}
        <TooltipWrapper class="ml-2">
          <template #trigger>
            <a :href="tooltipLink" target="_blank"><CircleHelpIcon class="lucide-icon-s text-icon-subtlest" /></a>
          </template>
          <template #content>
            <div v-html="tooltipText"></div>
          </template>
        </TooltipWrapper>
        <template #actions>
          <RouterLink
            class="btn-secondary-color-s mr-2"
            data-testid="cancel-button"
            :to="{ name: ReviewsRouteName.ReviewsProductViewPricing, params: { productId: product.id } }"
            @click="track('profile_update_cancel', { surface: 'editor', entity_type: 'tool', entity_id: product.id })"
          >
            {{ t('Reviews.product.action.cancel') }}
          </RouterLink>
          <button class="btn-primary-color-s" data-testid="save-button" @click="onSubmit">{{ t('Reviews.product.action.save') }}</button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="py-6">
      <div class="mb-6">
        <RadioButton v-model="v$.noPricingPlans.$model" class="mr-4" data-testid="no-pricing-plan-btn" :value="true">
          {{ t('Reviews.product.edit.pricing.noPricingPlans.yes') }}
        </RadioButton>
        <RadioButton v-model="v$.noPricingPlans.$model" data-testid="pricing-plan-btn" :value="false">
          {{ t('Reviews.product.edit.pricing.noPricingPlans.no') }}
        </RadioButton>
      </div>
      <div v-if="formData.noPricingPlans" class="grid grid-cols-2 gap-6">
        <div>
          <div v-for="lang in ALLOWED_LOCALES" :key="lang">
            <VuelidateField :validation="v$.noPricingPlansHint[lang]">
              <template #default="{ error, state }">
                <FieldWrapper :for="`no-pricing-plan-description-${lang}`" :error="error" hint=" ">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <div>{{ t('Reviews.product.edit.pricing.noPricingPlansHint.label') }}</div>
                      <ChipBadge rounded class="uppercase" size="s">{{ lang }}</ChipBadge>
                    </div>
                  </template>

                  <template #default="{ id }">
                    <div class="mt-2 mb-8">
                      <TextareaField
                        :id="id"
                        v-model="v$.noPricingPlansHint[lang].$model"
                        :data-testid="`no-pricing-plan-description-${lang}`"
                        :state="state"
                        rows="5"
                      />
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
        </div>
        <MessageBanner
          class="mt-7 h-fit"
          :headline="t('Reviews.product.edit.pricing.noPricingPlansHint.info.headline').toString()"
          :message="t('Reviews.product.edit.pricing.noPricingPlansHint.info.message').toString()"
          type="info"
        />
      </div>
      <div>
        <div class="pb-4 text-body-m-bold">{{ t('Reviews.product.edit.pricing.pricingOptions.label') }}</div>
        <CheckBox v-model="v$.showDisclaimer.$model" data-testid="show-disclaimer-check" class="w-full pb-4">
          {{ t('Reviews.product.edit.pricing.showDisclaimer.label') }}
        </CheckBox>
        <div class="-mt-2 mb-4 -ml-0.5 flex gap-x-1 text-main">
          <InfoIcon class="ml-0! ml-1 inline-block text-icon-primary" />
          {{ t('Reviews.product.edit.pricing.disclaimer.info.message') }}
        </div>
        <SlideTransition>
          <div v-if="formData.showDisclaimer" class="pb-2">
            <div class="mb-4 grid grid-cols-2 gap-6">
              <div v-for="lang in ALLOWED_LOCALES" :key="lang">
                <VuelidateField :validation="v$.disclaimerText[lang]">
                  <template #default="{ error, state }">
                    <FieldWrapper for="disclaimer-txt-${lang}" :error="error" hint=" ">
                      <template #label>
                        <div class="flex items-center justify-between">
                          <div>{{ t('Reviews.product.edit.pricing.disclaimerText.label') }}</div>
                          <ChipBadge rounded class="uppercase" size="s">{{ lang }}</ChipBadge>
                        </div>
                      </template>

                      <template #default="{ id }">
                        <div class="mt-2">
                          <InputField
                            :id="id"
                            v-model="v$.disclaimerText[lang].$model"
                            :data-testid="`no-pricing-plan-disclaimer-txt-${lang}`"
                            type="text"
                            :state="state"
                            :placeholder="t('Reviews.product.edit.pricing.disclaimerText.placeholder').toString()"
                          />
                        </div>
                      </template>
                    </FieldWrapper>
                  </template>
                </VuelidateField>
              </div>
            </div>
            <FeatureFencingWrapper
              :fenced="disclaimerUrlFenced"
              :label="t('Reviews.product.view.pricingEdit.disclaimerUrlFencing.label')"
              :upgrade-cta="t('Reviews.product.view.pricingEdit.disclaimerUrlFencing.upgradeCta')"
              :upgrade-cta-link="t('Reviews.product.view.pricingEdit.disclaimerUrlFencing.upgradeCtaLink')"
              placement="disclaimer_url"
              entity-type="tool"
              :entity-id="product.id"
            >
              <div class="grid grid-cols-2 gap-6">
                <div v-for="lang in ALLOWED_LOCALES" :key="lang">
                  <VuelidateField :validation="v$.disclaimerUrl[lang]">
                    <template #default="{ error, state }">
                      <FieldWrapper for="disclaimer-url-${lang}" :error="error">
                        <template #label>
                          <div class="flex items-center justify-between">
                            <div>{{ t('Reviews.product.edit.pricing.disclaimerUrl.label') }}</div>
                            <ChipBadge rounded class="uppercase" size="s">{{ lang }}</ChipBadge>
                          </div>
                        </template>

                        <template #default="{ id }">
                          <div class="mt-2">
                            <InputField
                              :id="id"
                              v-model="v$.disclaimerUrl[lang].$model"
                              :data-testid="`no-pricing-plan-disclaimer-url-${lang}`"
                              type="text"
                              :state="state"
                              :disabled="disclaimerUrlFenced"
                              :placeholder="t('Reviews.product.edit.pricing.disclaimerUrl.placeholder').toString()"
                            />
                          </div>
                        </template>
                      </FieldWrapper>
                    </template>
                  </VuelidateField>
                </div>
              </div>
            </FeatureFencingWrapper>
          </div>
        </SlideTransition>
        <CheckBox v-model="v$.freeDemo.$model" class="w-full pt-4 pb-4" data-testid="free-demo-check">
          {{ t('Reviews.product.edit.pricing.freeDemo.label') }}
        </CheckBox>
        <CheckBox v-model="v$.freeTrial.$model" class="w-full pb-4" data-testid="free-trial-check">
          {{ t('Reviews.product.edit.pricing.freeTrial.label') }}
        </CheckBox>
        <SlideTransition>
          <div v-if="formData.freeTrial" class="grid grid-cols-2 gap-6 pt-2 pb-6">
            <VuelidateField :validation="v$.trialUnit">
              <template #default="{ error, state }">
                <FieldWrapper
                  for="trial-unit"
                  :label="t('Reviews.product.edit.pricing.trialUnit.label').toString()"
                  :error="error"
                  data-testid="trial-unit-select"
                  hint=" "
                >
                  <template #default="{ id: inputId }">
                    <SelectField
                      :id="inputId"
                      v-model="v$.trialUnit.$model"
                      :placeholder="t('Reviews.product.edit.pricing.trialUnit.placeholder').toString()"
                      :state="state"
                    >
                      <option
                        v-for="period in productOptions.trialUnits"
                        :key="period"
                        :value="period"
                        :data-testid="`trial-select-${period}`"
                      >
                        {{ t(`Reviews.product.edit.pricing.trialUnit.${period}`) }}
                      </option>
                    </SelectField>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
            <VuelidateField :validation="v$.trialPeriod">
              <template #default="{ error, state }">
                <FieldWrapper
                  for="trialPeriod"
                  :label="t('Reviews.product.edit.pricing.trialPeriod.label').toString()"
                  :error="error"
                  hint=" "
                >
                  <template #default="{ id }">
                    <InputField
                      :id="id"
                      v-model.number="v$.trialPeriod.$model"
                      type="number"
                      min="1"
                      :state="state"
                      :placeholder="t('Reviews.product.edit.pricing.trialPeriod.placeholder').toString()"
                      data-testid="trial-period-input"
                    />
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
        </SlideTransition>
      </div>
      <div v-if="!formData.noPricingPlans">
        <div class="pb-4 text-body-m-bold">{{ t('Reviews.product.edit.pricing.pricingPlans.label') }}</div>
        <ExpandableView
          v-for="(plan, index) in formData.pricingPlans"
          :key="index"
          ref="pricingPlansRef"
          class="pb-2 transition-opacity duration-75 ease-(--motion-easing-standard)"
          :disabled="plan._destroy"
        >
          <template #name>
            <span class="flex min-w-0 items-center gap-2">
              <span class="truncate">{{
                plan.name[ALLOWED_LOCALES[0]] || t('Reviews.product.edit.pricing.pricingPlans.name', { number: index + 1 })
              }}</span>
              <ChipBadge
                v-if="plan.promoted && plan.promotedLabel"
                class="shrink-0"
                color="purple"
                size="s"
                rounded
                :data-testid="`pricing-plan-promoted-badge-${index}`"
              >
                {{ promotedLabelText(plan.promotedLabel) }}
              </ChipBadge>
            </span>
          </template>
          <template #actions>
            <button
              v-if="!plan._destroy"
              class="btn-link-neutral-m"
              :data-testid="`delete-pricing-plan-button-${index}`"
              @click.stop="deletePricingPlan(index)"
            >
              <span>{{ t('Reviews.product.edit.pricing.actions.deletePlan') }}</span>
              <Trash2Icon class="ml-1 lucide-icon-s" />
            </button>
            <button v-else class="btn-link-neutral-m" @click.stop="undoDeletePricingPlan(index)">
              <span>{{ t('Reviews.product.edit.pricing.actions.undoDeletePlan') }}</span>
              <UndoIcon class="ml-1 lucide-icon-s" />
            </button>
            <button class="btn-link-neutral-m ml-3" :disabled="index === 0" @click.stop="moveUp(index)">
              <ArrowUpIcon class="ml-1 lucide-icon-s" />
            </button>
            <button class="btn-link-neutral-m ml-3" :disabled="index === formData.pricingPlans.length - 1" @click.stop="moveDown(index)">
              <ArrowDownIcon class="ml-1 lucide-icon-s" />
            </button>
          </template>
          <div class="grid grid-cols-2 gap-6">
            <PricingPlanForm
              :key="plan._vid"
              :value="formData.pricingPlans[index]"
              :currencies-options="productOptions.currencies"
              :pricing-text-options="productOptions.pricingTextOptions"
              :subscription-modes="productOptions.pricingSubscriptionModes"
              :pricing-enabled="!formData.noPricingPlans"
              @input="formData.pricingPlans[index] = $event"
              @promoted-on="onPromotedOn(index)"
            />
            <div class="bg-surface-elevation-2">
              <PricingPlanCardPreview
                v-for="lang in ALLOWED_LOCALES"
                :key="lang"
                :lang="lang"
                :preview-name="t('Reviews.product.edit.pricing.pricingPlans.name', { number: index + 1 }).toString()"
                :pricing="formData.pricingPlans[index]"
                :currencies-options="productOptions.currencies"
                :pricing-text-options="productOptions.pricingTextOptions"
                :subscription-modes="productOptions.pricingSubscriptionModes"
              />
            </div>
          </div>
        </ExpandableView>

        <button
          v-if="formData.pricingPlans.length < PRICING_PLANS_LIMIT"
          class="btn-primary-color-s mt-2"
          data-testid="add-pricing-plan"
          @click.prevent="addPricingPlan"
        >
          <CirclePlusIcon class="mr-2" />
          {{ t('Reviews.product.edit.pricing.actions.addPlan', formData.pricingPlans.length + 1) }}
        </button>
      </div>
    </div>
  </ContentCard>
</template>
<script setup lang="ts">
import {
  MessageBanner,
  RadioButton,
  CheckBox,
  SlideTransition,
  ChipBadge,
  TooltipWrapper,
  FieldWrapper,
  InputField,
  SelectField,
  TextareaField,
} from '@ramp106/omrjs-core-ui'
import { ArrowUpIcon, ArrowDownIcon, CircleHelpIcon, CirclePlusIcon, InfoIcon, Trash2Icon, UndoIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { ref, computed, reactive, watch, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import ReviewsProductTabs from '@/components/ReviewsManagement/ReviewsProductTabs.vue'
import type { ProductData, PromotedLabelEnum, TranslationAttributes } from '@/gql/reviews'
import { ProductTrialUnitEnum, useReviewsProductOptionsQuery, useUpdateReviewsProductWithPricingPlansMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import {
  ALLOWED_LOCALES,
  mapTranslationsArrToObj,
  mapPerksTranslations,
  mapPerksFormDataToPerksTranslations,
} from '@/helpers/translationsHelper'
import { buildTranslationFieldsRules } from '@/helpers/validationHelper'
import { ReviewsRouteName } from '@/router/types'
import { requiredIf, url, maxLength, minLength, numeric, minValue } from '@/services/validators'
import { reloadReviewsProductKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import ExpandableView from '@/ui/ExpandableView.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

import PricingPlanCardPreview from './PricingPlanCardPreview.vue'
import PricingPlanForm from './PricingPlanForm.vue'
import type { PricingPlanFormData } from '../types'

interface FormData {
  freeDemo: boolean | undefined
  freeTrial: boolean | undefined
  trialUnit: ProductTrialUnitEnum | undefined
  trialPeriod: number | undefined
  showDisclaimer: boolean
  disclaimerText: Record<string, string>
  disclaimerUrl: Record<string, string>
  noPricingPlans: boolean
  noPricingPlansHint: Record<string, string>
  pricingPlans: PricingPlanFormData[]
}

const DISCLAIMER_MIN_LENGTH = 3
const DISCLAIMER_MAX_LENGTH = 120
const PRICING_PLANS_LIMIT = 20

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const router = useRouter()
const reloadReviewsProduct = inject(reloadReviewsProductKey) as () => void
const pricingPlansRef = ref<(typeof ExpandableView)[] | undefined>()

const tooltipText = t('Reviews.product.view.pricingEdit.pricingHelp')
const tooltipLink = t('Reviews.product.view.pricingEdit.pricingHelpLink')

const disclaimerUrlFenced = computed(() => !props.product.configFlags.pricingDisclaimerLinkAllowed)

const { result: productOptionsQuery } = useReviewsProductOptionsQuery({ clientId: 'reviews' })

const productOptions = computed(() => {
  const trialUnits = [ProductTrialUnitEnum.Day, ProductTrialUnitEnum.Week, ProductTrialUnitEnum.Month, ProductTrialUnitEnum.Year]
  if (productOptionsQuery.value?.manage?.options) {
    return {
      pricingTextOptions: productOptionsQuery.value.manage.options.pricingTextOptions.slice(0).sort((a, b) => a.name.localeCompare(b.name)),
      pricingSubscriptionModes: productOptionsQuery.value.manage.options.pricingSubscriptionModes
        .slice(0)
        .sort((a, b) => a.name.localeCompare(b.name)),
      currencies: productOptionsQuery.value.manage.options.currencies,
      trialUnits,
    }
  }

  return {
    pricingTextOptions: [],
    pricingSubscriptionModes: [],
    currencies: [],
    trialUnits,
  }
})

const formData = reactive<FormData>({
  freeDemo: props.product.freeDemo ?? false,
  freeTrial: props.product.freeTrial ?? false,
  trialUnit: props.product.trialUnit ?? undefined,
  trialPeriod: props.product.trialPeriod ?? undefined,
  showDisclaimer: props.product.showDisclaimer ?? false,
  disclaimerText: { ...props.product.disclaimerTextTranslationsMap },
  disclaimerUrl: { ...props.product.disclaimerUrlTranslationsMap },
  noPricingPlans: props.product.noPricingPlans ?? false,
  noPricingPlansHint: { ...props.product.noPricingPlansHintTranslationsMap },
  pricingPlans: [
    ...props.product.pricingPlans.map((plan, index) => ({
      _destroy: undefined,
      _vid: uuidv4(),
      id: plan.id,
      description: { ...mapTranslationsArrToObj(plan.descriptionTranslations) },
      name: { ...mapTranslationsArrToObj(plan.nameTranslations) },
      perkList: mapPerksTranslations(plan.perksTranslations),
      priceCents: plan.priceCents ?? undefined,
      priceCurrency: plan.priceCurrency ?? undefined,
      pricingSubscriptionModeId: plan.pricingSubscriptionModeId ?? undefined,
      pricingTextOptionId: plan.pricingTextOptionId ?? undefined,
      promoted: plan.promoted,
      promotedLabel: plan.promotedLabel ?? null,
      startingPrice: plan.startingPrice,
      titleIcon: plan.titleIcon ?? null,
      position: plan.position ?? index,
    })),
  ].sort((a, b) => a.position - b.position),
})

const rules = computed<ValidationArgs<FormData>>(() => ({
  freeDemo: {},
  freeTrial: {},
  trialUnit: { requiredIf: requiredIf(() => !!formData.freeTrial) },
  trialPeriod: { requiredIf: requiredIf(() => !!formData.freeTrial), numeric, minValue: minValue(1) },
  showDisclaimer: {},
  disclaimerText: {
    ...buildTranslationFieldsRules(
      {
        minLength: minLength(DISCLAIMER_MIN_LENGTH),
        maxLength: maxLength(DISCLAIMER_MAX_LENGTH),
        requiredIf: requiredIf(() => formData.showDisclaimer),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules(
      {
        minLength: minLength(DISCLAIMER_MIN_LENGTH),
        maxLength: maxLength(DISCLAIMER_MAX_LENGTH),
      },
      ALLOWED_LOCALES.slice(1),
    ),
  },
  disclaimerUrl: {
    ...buildTranslationFieldsRules(
      {
        url,
        requiredIf: requiredIf(() => formData.showDisclaimer && !disclaimerUrlFenced.value),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules({ url }, ALLOWED_LOCALES.slice(1)),
  },
  noPricingPlans: {},
  noPricingPlansHint: buildTranslationFieldsRules({}),
  pricingPlans: {},
}))

const v$ = useVuelidate(rules, formData)

function collapseAllPricingPlans() {
  if (pricingPlansRef.value) {
    pricingPlansRef.value.forEach((ref) => ref.close())
  }
}

function animatePositionChange(index: number) {
  collapseAllPricingPlans()
  pricingPlansRef.value?.[index].$el.classList.add('opacity-10')
  setTimeout(() => {
    formData.pricingPlans.sort((a, b) => a.position - b.position)
    pricingPlansRef.value?.[index].$el.classList.remove('opacity-10')
  }, 300)
}

function addPricingPlan() {
  track('profile_item_add', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id, item_type: 'pricing_plan' })
  formData.pricingPlans.push({
    _destroy: undefined,
    _vid: uuidv4(),
    description: ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: '' }), {}),
    id: undefined,
    name: ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: '' }), {}),
    perkList: [ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: { name: '' } }), {})],
    priceCents: undefined,
    priceCurrency: undefined,
    pricingSubscriptionModeId: undefined,
    pricingTextOptionId: undefined,
    promoted: false,
    promotedLabel: null,
    startingPrice: false,
    titleIcon: null,
    position: formData.pricingPlans.length,
  })
  collapseAllPricingPlans()
}

function promotedLabelText(label: PromotedLabelEnum): string {
  return t(`Reviews.product.edit.pricing.promotedLabel.value.${label}`).toString()
}

function onPromotedOn(promotedIndex: number) {
  formData.pricingPlans.forEach((plan, index) => {
    if (index !== promotedIndex) {
      plan.promoted = false
    }
  })
}

function deletePricingPlan(index: number) {
  if (formData.pricingPlans[index].id) {
    formData.pricingPlans[index]['_destroy'] = true
  } else {
    formData.pricingPlans.splice(index, 1)
  }
}

function undoDeletePricingPlan(index: number) {
  if (formData.pricingPlans[index].id) {
    formData.pricingPlans[index]['_destroy'] = undefined
  }
}

function moveUp(index: number) {
  if (index === 0) {
    return
  }
  formData.pricingPlans[index - 1].position = formData.pricingPlans[index - 1].position + 1
  formData.pricingPlans[index].position = formData.pricingPlans[index].position - 1
  animatePositionChange(index - 1)
  animatePositionChange(index)
}

function moveDown(index: number) {
  if (index === formData.pricingPlans.length - 1) {
    return
  }
  formData.pricingPlans[index].position = formData.pricingPlans[index].position + 1
  formData.pricingPlans[index + 1].position = formData.pricingPlans[index + 1].position - 1
  animatePositionChange(index)
  animatePositionChange(index + 1)
}

watch(
  () => formData.pricingPlans.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      setTimeout(() => {
        if (!pricingPlansRef.value) {
          return
        }
        const lastRef = pricingPlansRef.value[pricingPlansRef.value.length - 1]
        if (lastRef) {
          lastRef.open()
          lastRef.$el.scrollIntoView({ behavior: 'smooth' })
        }
      }, 50)
    }
  },
)

const {
  mutate: mutateProductWithPricingPlans,
  loading: loadingUpdateProduct,
  onDone,
  onError,
} = useUpdateReviewsProductWithPricingPlansMutation({
  clientId: 'reviews',
})

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id })
  v$.value.$touch()
  if (v$.value.$invalid) {
    showNotification(t('Reviews.product.notifications.invalidData'), 'error')
    return
  }

  const freeTrial = formData.freeTrial
  let trialPeriod = undefined
  let trialUnit = undefined

  if (freeTrial !== undefined) {
    trialPeriod = freeTrial ? formData.trialPeriod : null
    trialUnit = freeTrial ? formData.trialUnit : null
  }

  const noPricingPlansHintTranslations: TranslationAttributes[] = []
  const disclaimerTextTranslations: TranslationAttributes[] = []
  const disclaimerUrlTranslations: TranslationAttributes[] = []

  ALLOWED_LOCALES.forEach((locale) => {
    noPricingPlansHintTranslations.push({ locale, value: formData.noPricingPlansHint[locale] })
    disclaimerTextTranslations.push({ locale, value: formData.disclaimerText[locale] })
    disclaimerUrlTranslations.push({ locale, value: formData.disclaimerUrl[locale] })
  })

  const pricingPlansAttributes = formData.pricingPlans.map((plan, index) => ({
    _destroy: plan._destroy,
    descriptionTranslations: ALLOWED_LOCALES.map((locale) => ({ locale, value: plan.description[locale] })),
    id: plan.id,
    nameTranslations: ALLOWED_LOCALES.map((locale) => ({ locale, value: plan.name[locale] })),
    priceCents: plan.priceCents,
    priceCurrency: plan.priceCurrency,
    pricingSubscriptionModeId: plan.pricingSubscriptionModeId,
    pricingTextOptionId: plan.pricingTextOptionId,
    promoted: plan.promoted,
    promotedLabel: plan.promoted ? plan.promotedLabel : null,
    startingPrice: plan.startingPrice,
    titleIcon: plan.titleIcon,
    position: index,
    perksTranslations: mapPerksFormDataToPerksTranslations(plan.perkList),
  }))

  await mutateProductWithPricingPlans({
    id: props.product.id,
    attributes: {
      noPricingPlans: formData.noPricingPlans,
      noPricingPlansHintTranslations,
      showDisclaimer: formData.showDisclaimer,
      disclaimerTextTranslations,
      disclaimerUrlTranslations: disclaimerUrlFenced.value ? undefined : disclaimerUrlTranslations,
      freeDemo: formData.freeDemo,
      freeTrial,
      trialPeriod,
      trialUnit,
    },
    pricing: { pricingPlansAttributes, productId: props.product.id },
  })
}

onDone(() => {
  reloadReviewsProduct()
  showNotification(t('Reviews.product.notifications.savedSuccessfully', { name: props.product.title }), 'success')
  router.push({ name: ReviewsRouteName.ReviewsProductViewPricing, params: { productId: props.product.id } })
})

onError((error) => showError(error))

const showError = (error: Error) => {
  if (error) showNotification(error.message, 'error')
}
</script>
